/**
 * Showing an email's HTML without letting it do anything (ADR-180 in platform).
 *
 * An email that arrived is kept with its HTML as it was sent, and core-api never cleans it: anyone
 * can send one, so it is the console's job to draw it where it cannot act. It is drawn only inside
 * an `<iframe sandbox="">` (no script, no form, no popup, no way out to the page), from a document
 * that starts with a policy allowing nothing to be fetched but inline styles and `data:` pictures,
 * so a remote picture can never tell the sender the email was opened.
 *
 * Nothing in the frame is a link. A sandbox without flags still lets a frame go to another page
 * itself, and an email has more ways to ask for that than any list of attributes would catch: a
 * `target="_self"`, an SVG link (which reads no `<base target>`), a refresh, or a link inside a
 * `<template shadowrootmode>` that the frame turns into a live shadow root but a query from outside
 * never sees. So the HTML is read first in a document of its own, never attached to the page, and
 * every link address, target and form action is taken off every element, and every template,
 * refresh, base, stylesheet link, script and nested frame taken out. `<base target="_blank">` stays
 * as a second guard. The links are listed under the frame as plain text instead, for the rep to
 * copy if they want to open one.
 *
 * Pure but for the parser, which the browser has; for `test/email-view.test.ts`.
 */

/** What the frame's document starts with: the policy first, so it holds before anything the email
 *  says, then the base that sends a link nowhere. */
export const EMAIL_FRAME_HEAD =
  `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data:; style-src 'unsafe-inline'">` +
  `<base target="_blank">`;

/** The elements that could fetch, navigate or run, whatever the policy says of them. A template is
 *  one because its content is out of a query's reach and can become a live shadow root. */
const TAKEN_OUT = "meta, base, link, script, iframe, frame, frameset, object, embed, portal, template";
/** The attributes that send the frame, or a form in it, somewhere: on any element, SVG's too. */
const NO_WHERE = ["href", "xlink:href", "target", "formtarget", "action", "formaction", "ping"];

type Parser = new () => Pick<DOMParser, "parseFromString">;

function browserParser(): Parser | undefined {
  return typeof window !== "undefined" && typeof window.DOMParser === "function" ? window.DOMParser : undefined;
}

/**
 * The frame's `srcDoc` for an email's HTML. Without a parser (outside a browser) the HTML follows
 * the head as it came, still under the sandbox and the policy.
 */
export function emailFrameDoc(html: string, parser: Parser | undefined = browserParser()): string {
  if (!parser) return EMAIL_FRAME_HEAD + html;
  const doc = new parser().parseFromString(html, "text/html");
  for (const el of Array.from(doc.querySelectorAll(TAKEN_OUT))) el.remove();
  for (const el of Array.from(doc.querySelectorAll("*"))) {
    for (const name of NO_WHERE) el.removeAttribute(name);
    // Namespaced spellings (`xlink:href` read as XLink, `xml:base`), whatever their prefix.
    for (const attr of Array.from(el.attributes)) {
      if (/(^|:)(href|base)$/i.test(attr.name)) el.removeAttribute(attr.name);
    }
  }
  return EMAIL_FRAME_HEAD + doc.documentElement.outerHTML;
}

const HREF = /[\s/:]href\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))/gi;

/** The most links listed under the frame: a newsletter can carry hundreds. */
export const MAX_EMAIL_LINKS = 50;

/**
 * The web and mail links an email's HTML carries, in order, each once, for the list under the frame.
 * Read as text, never followed. Anything else (`javascript:`, a relative path, `cid:`) is left out,
 * as it opens nothing a rep could use.
 */
export function emailLinks(html: string): string[] {
  const out: string[] = [];
  for (const match of html.matchAll(HREF)) {
    const raw = (match[1] ?? match[2] ?? match[3] ?? "").trim();
    const url = raw.replace(/&amp;|&#0*38;|&#x0*26;/gi, "&");
    if (!/^(https?:\/\/|mailto:)/i.test(url) || out.includes(url)) continue;
    out.push(url);
    if (out.length === MAX_EMAIL_LINKS) break;
  }
  return out;
}
